import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  listCampaigns,
  getTransferDurations,
  getRepCoaching,
  type RepCoachingCall,
} from "../api/campaigns";

const BUCKETS: [string, keyof import("../api/campaigns").CampaignTransferDurations][] = [
  ["Connected", "b_connected_nodur"],
  ["<10s", "b_under_10s"],
  ["10s–2m", "b_10s_2m"],
  ["2–5m", "b_2m_5m"],
  ["5–10m", "b_5m_10m"],
  ["10–15m", "b_10m_15m"],
  ["15–20m", "b_15m_20m"],
  ["20m+", "b_over_20m"],
];

function fmtSec(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function DashboardPage() {
  const { data } = useQuery({ queryKey: ["campaigns"], queryFn: () => listCampaigns() });
  const campaigns = data ?? [];
  const running = campaigns.filter((c) => c.status === "running" || c.status === "active").length;
  const cards = [
    { label: "Total campaigns", value: campaigns.length },
    { label: "Running now", value: running },
  ];

  const [window, setWindow] = useState("today");
  const { data: dur } = useQuery({
    queryKey: ["transfer-durations", window],
    queryFn: () => getTransferDurations(window),
    refetchInterval: 30000,
  });
  const rows = dur?.campaigns ?? [];

  const { data: coaching } = useQuery({
    queryKey: ["rep-coaching"],
    queryFn: () => getRepCoaching(),
    refetchInterval: 60000,
  });

  const [callFilter, setCallFilter] = useState<"all" | "rep" | "hold_drop">("rep");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const allCalls: RepCoachingCall[] = coaching?.calls ?? [];
  const filteredCalls = allCalls.filter((c) => {
    if (callFilter === "rep" && c.stage !== "rep_conversation") return false;
    if (callFilter === "hold_drop" && c.stage !== "hold_queue_drop") return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${c.phone} ${c.did} ${c.transcript} ${c.uniroma?.outcome ?? ""} ${c.uniroma?.summary ?? ""} ${c.uniroma?.what_rep_could_do_better ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-4">Dashboard</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-lg shadow p-5">
              <div className="text-3xl font-bold text-brand">{c.value}</div>
              <div className="text-sm text-gray-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h2 className="font-semibold">Transfer call durations</h2>
            <p className="text-[11px] text-gray-400">
              Connected-call length per campaign. &lt;10s = talk-off / instant hang-ups; longer buckets are real conversations.
            </p>
          </div>
          <div className="flex gap-1">
            {[
              ["today", "Today"],
              ["24h", "24h"],
              ["all", "All"],
            ].map(([w, label]) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={`px-2.5 py-1 text-xs rounded ${
                  window === w ? "bg-brand text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Campaign</th>
                <th className="px-4 py-2 text-right">Transfers</th>
                {BUCKETS.map(([label]) => (
                  <th key={label} className="px-4 py-2 text-right">{label}</th>
                ))}
                <th className="px-4 py-2 text-right">% reached agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const real = r.transfers > 0 ? Math.round(((r.transfers - r.b_under_10s) / r.transfers) * 100) : 0;
                const realClass = real >= 90 ? "text-green-700" : real >= 70 ? "text-amber-600" : "text-red-600";
                return (
                  <tr key={r.campaign_id}>
                    <td className="px-4 py-2 font-medium">{r.campaign_name}</td>
                    <td className="px-4 py-2 text-right font-semibold">{r.transfers}</td>
                    {BUCKETS.map(([label, key]) => (
                      <td key={label} className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {(r[key] as number) || 0}
                      </td>
                    ))}
                    <td className={`px-4 py-2 text-right font-semibold tabular-nums ${realClass}`}>{real}%</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={BUCKETS.length + 3} className="px-4 py-6 text-center text-gray-400">
                    No transfers in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {coaching?.available && coaching.funnel && (
        <div className="space-y-6">
          {/* Executive Rep Coaching Header Card */}
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-brand">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                    UniRoma AI Audit
                  </span>
                  <span className="text-xs text-gray-400">
                    Date: {coaching.date} · Campaign #{coaching.campaign_id}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mt-1">
                  Rep Performance & Call Transcript Coaching Report
                </h2>
              </div>
              {coaching.synthesis?.overall_rep_grade && (
                <div className="flex items-center gap-4 bg-gray-50 px-4 py-2.5 rounded-lg border border-gray-200">
                  <div className="text-center">
                    <div className="text-xs text-gray-500 uppercase">Rep Floor Grade</div>
                    <div className="text-2xl font-extrabold text-brand">
                      {coaching.synthesis.overall_rep_grade}
                    </div>
                  </div>
                  {coaching.synthesis.overall_rep_score_out_of_10 != null && (
                    <div className="text-center border-l border-gray-200 pl-4">
                      <div className="text-xs text-gray-500 uppercase">Avg QA Score</div>
                      <div className="text-2xl font-bold text-gray-800">
                        {coaching.synthesis.overall_rep_score_out_of_10}/10
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {coaching.synthesis?.executive_summary && (
              <p className="mt-3 text-sm text-gray-700 leading-relaxed bg-blue-50/60 p-3.5 rounded border border-blue-100">
                {coaching.synthesis.executive_summary}
              </p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Total Recorded Calls</div>
                <div className="text-lg font-bold text-gray-900">{coaching.funnel.total_recordings}</div>
                <div className="text-[11px] text-gray-400">
                  {coaching.funnel.outbound_transfers} xfer · {coaching.funnel.inbound_callbacks} inb
                </div>
              </div>
              <div className="bg-green-50 rounded p-3 border border-green-100">
                <div className="text-xs text-green-700 font-medium">Live Rep Conversations</div>
                <div className="text-lg font-bold text-green-800">{coaching.funnel.rep_conversations}</div>
                <div className="text-[11px] text-green-600">
                  Avg talk: {fmtSec(coaching.funnel.avg_rep_talk_sec)}
                </div>
              </div>
              <div className="bg-red-50 rounded p-3 border border-red-100">
                <div className="text-xs text-red-700 font-medium">Hold Queue Drops</div>
                <div className="text-lg font-bold text-red-800">{coaching.funnel.hold_queue_drops}</div>
                <div className="text-[11px] text-red-600">&ldquo;All agents busy&rdquo; hangup</div>
              </div>
              <div className="bg-amber-50 rounded p-3 border border-amber-100">
                <div className="text-xs text-amber-700 font-medium">Brief Contact / Drop</div>
                <div className="text-lg font-bold text-amber-800">{coaching.funnel.brief_contacts}</div>
                <div className="text-[11px] text-amber-600">Hung up early in pitch</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">IVR Pre-Press-3 Drops</div>
                <div className="text-lg font-bold text-gray-700">{coaching.funnel.ivr_hangups_pre_press3}</div>
                <div className="text-[11px] text-gray-400">Fixed via direct SIP route</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">&lt;10s Instant Hangups</div>
                <div className="text-lg font-bold text-gray-700">{coaching.funnel.short_hangups_under_10s}</div>
                <div className="text-[11px] text-gray-400">Talk-offs during transfer</div>
              </div>
            </div>
          </div>

          {/* What Reps Did Well & What Reps Could Do Better */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="font-bold text-green-800 text-base flex items-center gap-2 mb-3">
                <span>✓ What Reps Did Well Today</span>
              </h3>
              <div className="space-y-3">
                {(coaching.synthesis?.what_reps_did_well ?? []).map((item, i) => (
                  <div key={i} className="p-3 rounded bg-green-50/60 border border-green-100">
                    <div className="font-semibold text-sm text-green-900">{item.title}</div>
                    <div className="text-xs text-gray-700 mt-1 leading-relaxed">{item.detail}</div>
                  </div>
                ))}
              </div>

              {(coaching.synthesis?.operational_bottlenecks ?? []).length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <h4 className="font-bold text-amber-800 text-sm mb-2">
                    ⚡ Operational & Queue Bottlenecks Identified
                  </h4>
                  <div className="space-y-2.5">
                    {(coaching.synthesis?.operational_bottlenecks ?? []).map((b, i) => (
                      <div key={i} className="p-3 rounded bg-amber-50/70 border border-amber-200 text-xs">
                        <div className="font-semibold text-amber-900">{b.title}</div>
                        <div className="text-gray-700 mt-1">{b.finding}</div>
                        <div className="text-amber-900 font-medium mt-1">
                          Fix: {b.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="font-bold text-red-800 text-base flex items-center gap-2 mb-3">
                <span>🎯 What Reps Could Do Better (Actionable Coaching & Scripts)</span>
              </h3>
              <div className="space-y-3">
                {(coaching.synthesis?.what_reps_could_do_better ?? []).map((item, i) => (
                  <div key={i} className="p-3.5 rounded bg-red-50/40 border border-red-100">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-gray-900">{item.title}</span>
                      {item.impact && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-100 text-red-800">
                          {item.impact} Impact
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-700 mt-1">{item.problem}</p>
                    {item.fix_script && (
                      <div className="mt-2 p-2.5 rounded bg-white border border-gray-200 text-xs text-gray-800">
                        <span className="font-semibold text-brand block mb-0.5">
                          Recommended Rep Script / Action:
                        </span>
                        &ldquo;{item.fix_script}&rdquo;
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Call-by-Call Transcripts & UniRoma Evaluations */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Call-by-Call Transcripts & UniRoma Rep Scorecard ({filteredCalls.length})
                </h3>
                <p className="text-[11px] text-gray-400">
                  Click any call row to expand the full Whisper transcript and verbatim quote.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search phone, transcript, outcome…"
                  className="border border-gray-200 rounded px-2.5 py-1 text-xs w-56"
                />
                {[
                  ["rep", "Live Rep Calls"],
                  ["hold_drop", "Hold Queue Drops"],
                  ["all", "All Sampled"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setCallFilter(k as "all" | "rep" | "hold_drop")}
                    className={`px-2.5 py-1 text-xs rounded ${
                      callFilter === k
                        ? "bg-brand text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Type / Phone</th>
                    <th className="px-3 py-2">Length</th>
                    <th className="px-3 py-2">Outcome & Debt</th>
                    <th className="px-3 py-2 text-center">Score</th>
                    <th className="px-3 py-2">What Rep Did Well</th>
                    <th className="px-3 py-2">What Rep Could Do Better</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCalls.map((c) => {
                    const u = c.uniroma ?? {};
                    const isExpanded = expandedId === c.id;
                    const score = u.score;
                    const scoreBadge =
                      score == null
                        ? "bg-gray-100 text-gray-500"
                        : score >= 8
                        ? "bg-green-100 text-green-800"
                        : score >= 5
                        ? "bg-amber-100 text-amber-800"
                        : "bg-red-100 text-red-800";
                    return (
                      <>
                        <tr
                          key={c.id}
                          onClick={() => setExpandedId(isExpanded ? null : c.id)}
                          className="hover:bg-gray-50 cursor-pointer align-top"
                        >
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-1.5 ${
                                c.call_type === "outbound_transfer"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-teal-100 text-teal-800"
                              }`}
                            >
                              {c.call_type === "outbound_transfer" ? "Press-3 Xfer" : "Inbound CB"}
                            </span>
                            <span className="font-mono font-medium text-gray-800">
                              {c.phone || c.did || c.id}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap font-mono text-gray-700">
                            {fmtSec(c.duration_sec)}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-semibold text-gray-900">
                              {u.outcome || (c.stage === "hold_queue_drop" ? "Dropped in Hold Queue" : c.stage)}
                            </div>
                            {u.tax_debt_amount && u.tax_debt_amount !== "Unknown" && (
                              <div className="text-[11px] text-gray-500">
                                Debt: {u.tax_debt_amount}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded font-bold ${scoreBadge}`}>
                              {score != null ? `${score}/10` : "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-700 max-w-xs">
                            {u.what_rep_did_well || u.summary || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-gray-800 max-w-sm">
                            {u.what_rep_could_do_better ||
                              (c.stage === "hold_queue_drop"
                                ? "Caller waited in hold queue ('All agents busy') and hung up before a rep answered."
                                : "—")}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${c.id}-exp`} className="bg-gray-50/90">
                            <td colSpan={6} className="px-4 py-3 text-xs">
                              {u.summary && (
                                <div className="mb-2">
                                  <span className="font-semibold text-gray-700">Call Summary: </span>
                                  <span className="text-gray-800">{u.summary}</span>
                                </div>
                              )}
                              {u.key_quote && (
                                <div className="mb-2 italic text-gray-600">
                                  <span className="font-semibold not-italic text-gray-700">Key Quote: </span>
                                  &ldquo;{u.key_quote}&rdquo;
                                </div>
                              )}
                              <div className="font-semibold text-gray-700 mb-1">
                                Full Whisper Transcript ({c.filename}):
                              </div>
                              <div className="p-3 bg-white rounded border border-gray-200 font-mono text-[11px] text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {c.transcript || "(No speech detected)"}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
